import {
  useMutation,
  UseMutationResult,
  UseMutationOptions as UseMutationOptionsType,
} from '@tanstack/react-query';
import { PageTemplateAPI } from 'api/page-template';
import {
  CreatePageTemplatePayload,
  PageTemplate,
  PageType,
  SourceType,
  UpdatePageTemplatePayload,
} from 'api/page-template/types';

type UseMutationOptions<TData, TVariables> = Omit<
  UseMutationOptionsType<TData, Error, TVariables>,
  'mutationFn'
>;

type CreatePageTemplateMutationOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = UseMutationOptions<
  PageTemplate<PageTypeT, SourceTypeT>,
  CreatePageTemplatePayload<PageTypeT, SourceTypeT>
>;

const useCreatePageTemplateMutation = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  options?: CreatePageTemplateMutationOptions<PageTypeT, SourceTypeT>,
): UseMutationResult<
  PageTemplate<PageTypeT, SourceTypeT>,
  Error,
  CreatePageTemplatePayload<PageTypeT, SourceTypeT>
> => {
  return useMutation<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    CreatePageTemplatePayload<PageTypeT, SourceTypeT>
  >({
    mutationFn: (payload: CreatePageTemplatePayload<PageTypeT, SourceTypeT>) =>
      PageTemplateAPI.createPageTemplate(payload),
    ...options,
  });
};

type UpdatePageTemplateMutationOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = UseMutationOptions<
  PageTemplate<PageTypeT, SourceTypeT>,
  UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
>;

const useUpdatePageTemplateMutation = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  options?: UpdatePageTemplateMutationOptions<PageTypeT, SourceTypeT>,
): UseMutationResult<
  PageTemplate<PageTypeT, SourceTypeT>,
  Error,
  UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
> => {
  return useMutation<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
  >({
    mutationFn: (payload: UpdatePageTemplatePayload<PageTypeT, SourceTypeT>) =>
      PageTemplateAPI.updatePageTemplate<PageTypeT, SourceTypeT>(payload),
    ...options,
  });
};

type UseDeletePageTemplateMutationOptions = UseMutationOptions<void, number>;

const useDeletePageTemplateMutation = (
  queryOptions?: UseDeletePageTemplateMutationOptions,
): UseMutationResult<void, Error, number> =>
  useMutation({
    mutationFn: (templateId: number) =>
      PageTemplateAPI.deletePageTemplate(templateId),
    ...queryOptions,
  });

type UseUpdatePageTemplateOrderMutationOptions = UseMutationOptions<
  void,
  { templateId: number; order: number }
>;

const useUpdatePageTemplateOrderMutation = (
  queryOptions?: UseUpdatePageTemplateOrderMutationOptions,
): UseMutationResult<void, Error, { templateId: number; order: number }> =>
  useMutation({
    mutationFn: ({ templateId, order }) =>
      PageTemplateAPI.updatePageTemplateOrder(templateId, order),
    ...queryOptions,
  });

type UseUpdatePageTemplateStatusMutationOptions = UseMutationOptions<
  void,
  { templateId: number; status: boolean }
>;

const useUpdatePageTemplateStatusMutation = (
  queryOptions?: UseUpdatePageTemplateStatusMutationOptions,
): UseMutationResult<void, Error, { templateId: number; status: boolean }> =>
  useMutation({
    mutationFn: ({ templateId, status }) =>
      PageTemplateAPI.updatePageTemplateStatus(templateId, status),
    ...queryOptions,
  });

export {
  type UseDeletePageTemplateMutationOptions,
  type UseUpdatePageTemplateOrderMutationOptions,
  type UseUpdatePageTemplateStatusMutationOptions,
  type CreatePageTemplateMutationOptions,
  type UpdatePageTemplateMutationOptions,
  useDeletePageTemplateMutation,
  useUpdatePageTemplateOrderMutation,
  useUpdatePageTemplateStatusMutation,
  useCreatePageTemplateMutation,
  useUpdatePageTemplateMutation,
};
